import { OpClient, transfer } from '@toeverything/infra/op';
import type { Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { DummyConnection } from '../connection';
import {
  AwarenessFrontend,
  BlobFrontend,
  DocFrontend,
  IndexerFrontend,
} from '../frontend';
import {
  type AggregateOptions,
  type AggregateResult,
  type AwarenessRecord,
  type BlobRecord,
  type BlobStorage,
  type DocRecord,
  type DocStorage,
  type DocUpdate,
  type IndexerSchema,
  type ListedBlobRecord,
  type Query,
  type SearchOptions,
  type SearchResult,
} from '../storage';
import type { AwarenessSync } from '../sync/awareness';
import type { BlobSync } from '../sync/blob';
import type { DocSync } from '../sync/doc';
import type { IndexerPreferOptions, IndexerSync } from '../sync/indexer';
import type { StoreInitOptions, WorkerManagerOps, WorkerOps } from './ops';

export type { StoreInitOptions as WorkerInitOptions } from './ops';

export class StoreManagerClient {
  private readonly connections = new Map<
    string,
    {
      store: StoreClient;
      dispose: () => void;
    }
  >();

  constructor(private readonly client: OpClient<WorkerManagerOps>) {}

  open(key: string, options: StoreInitOptions) {
    const { port1, port2 } = new MessageChannel();

    const client = new OpClient<WorkerOps>(port1);
    const closeKey = uuid();

    const ready = this.client.call(
      'open',
      transfer(
        {
          key,
          closeKey,
          options,
          port: port2,
        },
        [port2]
      )
    );

    const connection = {
      store: new StoreClient(client, ready),
      dispose: () => {
        this.client.call('close', closeKey).catch(err => {
          console.error('error closing', err);
        });
        this.connections.delete(closeKey);
      },
    };

    this.connections.set(closeKey, connection);

    return connection;
  }

  dispose() {
    this.connections.forEach(connection => {
      connection.dispose();
    });
  }

  pause() {
    this.connections.forEach(connection => {
      connection.store.pauseSync().catch(err => {
        console.error('error pausing', err);
      });
    });
  }

  resume() {
    this.connections.forEach(connection => {
      connection.store.resumeSync().catch(err => {
        console.error('error resuming', err);
      });
    });
  }
}

export class StoreClient {
  constructor(
    private readonly client: OpClient<WorkerOps>,
    ready: Promise<unknown> = Promise.resolve()
  ) {
    this.docStorage = new WorkerDocStorage(this.client);
    this.blobStorage = new WorkerBlobStorage(this.client);
    this.docSync = new WorkerDocSync(this.client, ready);
    this.blobSync = new WorkerBlobSync(this.client);
    this.awarenessSync = new WorkerAwarenessSync(this.client);
    this.docFrontend = new DocFrontend(this.docStorage, this.docSync);
    this.blobFrontend = new BlobFrontend(this.blobStorage, this.blobSync);
    this.awarenessFrontend = new AwarenessFrontend(this.awarenessSync);
    this.indexerSync = new WorkerIndexerSync(this.client);
    this.indexerFrontend = new IndexerFrontend(this.indexerSync);
  }

  private readonly docStorage: WorkerDocStorage;
  private readonly blobStorage: WorkerBlobStorage;
  private readonly blobSync: WorkerBlobSync;
  private readonly awarenessSync: WorkerAwarenessSync;
  private readonly indexerSync: WorkerIndexerSync;

  /**
   * Direct access to doc sync for corruption$ events.
   */
  readonly docSync: WorkerDocSync;
  readonly docFrontend: DocFrontend;
  readonly blobFrontend: BlobFrontend;
  readonly awarenessFrontend: AwarenessFrontend;
  readonly indexerFrontend: IndexerFrontend;

  enableBatterySaveMode(): Promise<void> {
    return this.client.call('sync.enableBatterySaveMode');
  }
  disableBatterySaveMode(): Promise<void> {
    return this.client.call('sync.disableBatterySaveMode');
  }

  pauseSync() {
    return this.client.call('sync.pauseSync');
  }
  resumeSync() {
    return this.client.call('sync.resumeSync');
  }

  // sl-canvas2-8.24.21: Expose doc storage methods for integrity checking
  /**
   * Get a document by ID (for integrity checking).
   * Returns the full document record including binary data.
   */
  getDoc(docId: string): Promise<DocRecord | null> {
    return this.docStorage.getDoc(docId);
  }

  /**
   * Get all document timestamps (for integrity checking).
   * Returns a map of docId to last modified timestamp.
   */
  getDocTimestamps(after?: Date): Promise<Record<string, Date>> {
    return this.docStorage.getDocTimestamps(after);
  }

  /**
   * Wait until local document storage is ready for direct reads.
   */
  waitForLocalDocStorageConnected(): Promise<void> {
    return this.docStorage.connection.waitForConnected();
  }

  /**
   * Clear only local doc storage. Does not call remote delete.
   */
  resetLocalDoc(docId: string): Promise<void> {
    return this.docStorage.resetLocalDoc(docId);
  }
}

class WorkerDocStorage implements DocStorage {
  constructor(private readonly client: OpClient<WorkerOps>) {}
  spaceId = '';

  readonly storageType = 'doc';
  readonly isReadonly = false;

  async getDoc(docId: string) {
    return this.client.call('docStorage.getDoc', docId);
  }

  async getDocDiff(docId: string, state?: Uint8Array) {
    return this.client.call('docStorage.getDocDiff', { docId, state });
  }

  async pushDocUpdate(update: DocUpdate, origin?: string) {
    return this.client.call('docStorage.pushDocUpdate', { update, origin });
  }

  async getDocTimestamp(docId: string) {
    return this.client.call('docStorage.getDocTimestamp', docId);
  }

  async getDocTimestamps(after?: Date) {
    return this.client.call('docStorage.getDocTimestamps', after ?? null);
  }

  async deleteDoc(docId: string) {
    return this.client.call('docStorage.deleteDoc', docId);
  }

  /**
   * Clear only local IDB state for a doc (not remote).
   * Used after server-side compaction so the sync engine
   * re-pulls the fresh snapshot from the server.
   */
  async resetLocalDoc(docId: string) {
    return this.client.call('docStorage.resetLocalDoc', docId);
  }

  /**
   * Surgically repair a corrupt doc by removing only corrupt IDB entries.
   * Validates snapshot and each individual update, deletes only corrupt ones.
   * Preserves all valid unsynced data — safe for workspace root and all docs.
   */
  async repairDoc(docId: string) {
    return this.client.call('docStorage.repairDoc', docId);
  }

  subscribeDocUpdate(callback: (update: DocRecord, origin?: string) => void) {
    const subscription = this.client
      .ob$('docStorage.subscribeDocUpdate')
      .subscribe(value => {
        callback(value.update, value.origin);
      });
    return () => {
      subscription.unsubscribe();
    };
  }

  subscribeDocCompacted(
    _callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void {
    // Compacted events come through docSync.compacted$, not storage layer
    return () => {};
  }

  subscribePermissionsChanged(
    _callback: (info: {
      workspaceId: string;
      changeType: string;
      timestamp: number;
    }) => void
  ): () => void {
    // Permission events come through docSync.permissionsChanged$, not storage layer
    return () => {};
  }

  subscribeWorkspaceMetaChanged(
    _callback: (info: {
      workspaceId: string;
      changeType: string;
      docId: string;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void {
    // Workspace meta events come through docSync.workspaceMetaChanged$, not storage layer
    return () => {};
  }

  subscribeRecoveryCompleted(
    _callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Recovery events come through docSync.recoveryCompleted$, not storage layer
    return () => {};
  }

  subscribeDocSizeWarning(
    _callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void {
    // Doc size events come through docSync.docSizeWarning$, not storage layer
    return () => {};
  }

  subscribeDocSizeHardLimit(
    _callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void {
    // Doc size events come through docSync.docSizeHardLimit$, not storage layer
    return () => {};
  }

  subscribeEmbeddingDocStatus(
    _callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Embedding events come through docSync.embeddingDocStatus$, not storage layer
    return () => {};
  }

  subscribeEmbeddingWorkspaceProgress(
    _callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Embedding events come through docSync.embeddingWorkspaceProgress$, not storage layer
    return () => {};
  }

  subscribeKgUpdated(
    _callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ): () => void {
    // KG events come through docSync.kgUpdated$, not storage layer
    return () => {};
  }

  connection = new WorkerDocConnection(this.client);
}

class WorkerDocConnection extends DummyConnection {
  constructor(private readonly client: OpClient<WorkerOps>) {
    super();
  }

  promise: Promise<void> | undefined;

  override waitForConnected(): Promise<void> {
    if (this.promise) {
      return this.promise;
    }
    this.promise = this.client
      .call('docStorage.waitForConnected')
      .catch(err => {
        this.promise = undefined;
        throw err;
      });
    return this.promise;
  }
}

class WorkerBlobStorage implements BlobStorage {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  readonly storageType = 'blob';
  readonly isReadonly = false;

  get(key: string, _signal?: AbortSignal): Promise<BlobRecord | null> {
    return this.client.call('blobStorage.getBlob', key);
  }
  set(blob: BlobRecord, _signal?: AbortSignal): Promise<void> {
    return this.client.call('blobStorage.setBlob', blob);
  }

  delete(
    key: string,
    permanently: boolean,
    _signal?: AbortSignal
  ): Promise<void> {
    return this.client.call('blobStorage.deleteBlob', { key, permanently });
  }

  release(_signal?: AbortSignal): Promise<void> {
    return this.client.call('blobStorage.releaseBlobs');
  }

  list(_signal?: AbortSignal): Promise<ListedBlobRecord[]> {
    return this.client.call('blobStorage.listBlobs');
  }

  connection = new WorkerBlobConnection(this.client);
}

class WorkerBlobConnection extends DummyConnection {
  constructor(private readonly client: OpClient<WorkerOps>) {
    super();
  }

  promise: Promise<void> | undefined;

  override waitForConnected(): Promise<void> {
    if (this.promise) {
      return this.promise;
    }
    this.promise = this.client
      .call('blobStorage.waitForConnected')
      .catch(err => {
        this.promise = undefined;
        throw err;
      });
    return this.promise;
  }
}

class WorkerDocSync implements DocSync {
  constructor(
    private readonly client: OpClient<WorkerOps>,
    private readonly ready: Promise<unknown>
  ) {}

  get state$() {
    return this.client.ob$('docSync.state');
  }

  get corruption$() {
    return this.client.ob$('docSync.corruption');
  }

  // sl-affine-uhr: Expose compacted$ from worker
  get compacted$() {
    return this.client.ob$('docSync.compacted');
  }

  // sl-j143.5: Expose permission change events from worker
  get permissionsChanged$() {
    return this.client.ob$('docSync.permissionsChanged');
  }

  // Expose workspace meta change events from worker
  get workspaceMetaChanged$() {
    return this.client.ob$('docSync.workspaceMetaChanged');
  }

  // sl-lkdb.3.2: Expose recovery completed events from worker
  get recoveryCompleted$() {
    return this.client.ob$('docSync.recoveryCompleted');
  }

  // sl-lv2w.5: Expose doc size warning events from worker
  get docSizeWarning$() {
    return this.client.ob$('docSync.docSizeWarning');
  }

  // sl-lv2w.5: Expose doc size hard limit events from worker
  get docSizeHardLimit$() {
    return this.client.ob$('docSync.docSizeHardLimit');
  }

  // sl-00ro: Expose embedding doc status events from worker
  get embeddingDocStatus$() {
    return this.client.ob$('docSync.embeddingDocStatus');
  }

  // sl-00ro: Expose embedding workspace progress events from worker
  get embeddingWorkspaceProgress$() {
    return this.client.ob$('docSync.embeddingWorkspaceProgress');
  }

  get kgUpdated$() {
    return this.client.ob$('docSync.kgUpdated');
  }

  docState$(docId: string) {
    return this.client.ob$('docSync.docState', docId);
  }

  async waitForSynced(docId?: string, abort?: AbortSignal): Promise<void> {
    await this.client.call('docSync.waitForSynced', docId ?? null, abort);
  }

  /**
   * sl-sync-flush: Flush all pending updates and wait for backend ACK.
   */
  async flushAndWaitForSynced(
    docId: string,
    abort?: AbortSignal
  ): Promise<void> {
    await this.client.call('docSync.flushAndWaitForSynced', docId, abort);
  }

  // sl-suoi.1.2: Expose hot sync complete from worker
  get hotSyncComplete$() {
    return this.client.ob$('docSync.hotSyncComplete');
  }

  // sl-suoi.1.3: Dynamically add doc to sync queue
  addDoc(docId: string, source: import('../sync/doc').DocSyncRequestSource) {
    void this.ready
      .then(() => this.client.call('docSync.addDoc', { docId, source }))
      .catch(error => {
        console.error('Failed to enqueue document for sync', docId, error);
      });
  }

  async releaseDoc(
    docId: string,
    source: import('../sync/doc').DocSyncRequestSource
  ) {
    await this.ready;
    await this.client.call('docSync.releaseDoc', { docId, source });
  }

  debugDocState(docId: string) {
    return this.client.call('docSync.debugDocState', docId);
  }

  async removeDoc(docId: string): Promise<void> {
    await this.client.call('docSync.removeDoc', docId);
  }

  addPriority(docId: string, priority: number) {
    let disposed = false;
    let subscription: { unsubscribe(): void } | undefined;

    void this.ready
      .then(() => {
        if (disposed) {
          return;
        }
        subscription = this.client
          .ob$('docSync.addPriority', { docId, priority })
          .subscribe();
      })
      .catch(error => {
        console.error('Failed to prioritize document sync', docId, error);
      });

    return () => {
      disposed = true;
      subscription?.unsubscribe();
    };
  }

  resetSync(): Promise<void> {
    return this.client.call('docSync.resetSync');
  }

  async forceReconcile(_docId: string): Promise<never[]> {
    // Bidirectional diagnostic reconciliation is not exposed by the worker.
    return [];
  }

  async pullRemote(docId: string) {
    await this.ready;
    return this.client.call('docSync.pullRemote', docId);
  }
}

class WorkerBlobSync implements BlobSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}
  get state$() {
    return this.client.ob$('blobSync.state');
  }
  blobState$(blobId: string) {
    return this.client.ob$('blobSync.blobState', blobId);
  }

  downloadBlob(blobId: string): Promise<boolean> {
    return this.client.call('blobSync.downloadBlob', blobId);
  }
  uploadBlob(blob: BlobRecord, force?: boolean): Promise<true> {
    return this.client.call('blobSync.uploadBlob', { blob, force });
  }
  fullDownload(peerId?: string, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('blobSync.fullDownload', peerId ?? null)
        .subscribe({
          next() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
}

class WorkerAwarenessSync implements AwarenessSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  update(record: AwarenessRecord, origin?: string): Promise<void> {
    return this.client.call('awarenessSync.update', {
      awareness: record,
      origin,
    });
  }

  subscribeUpdate(
    id: string,
    onUpdate: (update: AwarenessRecord, origin?: string) => void,
    onCollect: () => Promise<AwarenessRecord | null>
  ): () => void {
    const subscription = this.client
      .ob$('awarenessSync.subscribeUpdate', id)
      .subscribe({
        next: update => {
          if (update.type === 'awareness-update') {
            onUpdate(update.awareness, update.origin);
          }
          if (update.type === 'awareness-collect') {
            onCollect()
              .then(record => {
                if (record) {
                  this.client
                    .call('awarenessSync.collect', {
                      awareness: record,
                      collectId: update.collectId,
                    })
                    .catch(err => {
                      console.error('error feedback collected awareness', err);
                    });
                }
              })
              .catch(err => {
                console.error('error collecting awareness', err);
              });
          }
        },
      });
    return () => {
      subscription.unsubscribe();
    };
  }
}

class WorkerIndexerSync implements IndexerSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  search<T extends keyof IndexerSchema, const O extends SearchOptions<T>>(
    table: T,
    query: Query<T>,
    options?: O & { prefer?: IndexerPreferOptions }
  ): Promise<SearchResult<T, O>> {
    return this.client.call('indexerSync.search', { table, query, options });
  }
  aggregate<T extends keyof IndexerSchema, const O extends AggregateOptions<T>>(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O & { prefer?: IndexerPreferOptions }
  ): Promise<AggregateResult<T, O>> {
    return this.client.call('indexerSync.aggregate', {
      table,
      query,
      field: field as string,
      options,
    });
  }
  search$<T extends keyof IndexerSchema, const O extends SearchOptions<T>>(
    table: T,
    query: Query<T>,
    options?: O & { prefer?: IndexerPreferOptions }
  ): Observable<SearchResult<T, O>> {
    return this.client.ob$('indexerSync.subscribeSearch', {
      table,
      query,
      options,
    });
  }
  aggregate$<
    T extends keyof IndexerSchema,
    const O extends AggregateOptions<T>,
  >(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O & { prefer?: IndexerPreferOptions }
  ): Observable<AggregateResult<T, O>> {
    return this.client.ob$('indexerSync.subscribeAggregate', {
      table,
      query,
      field: field as string,
      options,
    });
  }

  waitForCompleted(signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('indexerSync.waitForCompleted')
        .subscribe({
          complete() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
  waitForDocCompleted(docId: string, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('indexerSync.waitForDocCompleted', docId)
        .subscribe({
          complete() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
  get state$() {
    return this.client.ob$('indexerSync.state');
  }
  docState$(docId: string) {
    return this.client.ob$('indexerSync.docState', docId);
  }
  getDatabaseSubdocIds(ownerDocId: string): Promise<string[]> {
    return this.client.call('indexerSync.getDatabaseSubdocIds', ownerDocId);
  }
  addPriority(docId: string, priority: number) {
    const subscription = this.client
      .ob$('indexerSync.addPriority', { docId, priority })
      .subscribe();
    return () => {
      subscription.unsubscribe();
    };
  }
}
